import { motion, useReducedMotion } from "framer-motion";
import {
  BarChart3,
  CalendarCheck,
  ClipboardPlus,
  FileCheck2,
  Route,
  ShieldCheck,
  Smile,
  Stethoscope,
  Timer,
  WalletCards,
} from "lucide-react";
import PulsingPillIcon from "./PulsingPillIcon";
import {
  slowFadeUp,
  slowFadeUpStatic,
  slowStagger,
  slowStaggerStatic,
} from "../utils/animations";

export default function HowItWorks() {
  const prefersReducedMotion = useReducedMotion();
  const ctaStagger = prefersReducedMotion ? slowStaggerStatic : slowStagger;
  const ctaFade = prefersReducedMotion ? slowFadeUpStatic : slowFadeUp;
  const STEP_INTERVAL = 0.22;

  const stepRevealVariants = {
    hidden: { opacity: 0, y: 20 },
    show: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut",
        delay: index * STEP_INTERVAL,
      },
    }),
  };

  const connectorRevealVariants = {
    hidden: { scaleX: 0, opacity: 0.45 },
    show: (index) => ({
      scaleX: 1,
      opacity: 1,
      transition: {
        duration: 0.35,
        ease: "easeOut",
        // Connector to next step animates with that next step/card pair.
        delay: (index + 1) * STEP_INTERVAL,
      },
    }),
  };

  const journeySteps = [
    {
      number: "1",
      title: "Book Appointment",
      description:
        "Patients book appointments online or at the front desk in seconds.",
      accent: "bg-blue-100 text-blue-700",
      iconAccent: "text-blue-700",
      Icon: CalendarCheck,
    },
    {
      number: "2",
      title: "Consultation",
      description:
        "Doctors access complete patient history and record diagnoses seamlessly.",
      accent: "bg-emerald-100 text-emerald-700",
      iconAccent: "text-emerald-700",
      Icon: Stethoscope,
    },
    {
      number: "3",
      title: "Orders & Prescriptions",
      description:
        "Create prescriptions, lab tests, and radiology orders effortlessly.",
      accent: "bg-violet-100 text-violet-700",
      iconAccent: "text-violet-700",
      Icon: ClipboardPlus,
    },
    {
      number: "4",
      title: "Billing & Payment",
      description:
        "Automated billing with insurance handling and multiple payment options.",
      accent: "bg-amber-100 text-amber-800",
      iconAccent: "text-amber-800",
      Icon: WalletCards,
    },
    {
      number: "5",
      title: "Follow-up & Records",
      description:
        "All records are stored securely and follow-ups are easily managed.",
      accent: "bg-cyan-100 text-cyan-700",
      iconAccent: "text-cyan-700",
      Icon: FileCheck2,
    },
  ];

  const benefits = [
    {
      title: "Save Time",
      description: "Automate routine tasks and reduce manual work.",
      accent: "bg-sky-100 text-sky-700",
      Icon: Timer,
    },
    {
      title: "Reduce Errors",
      description: "Standardized workflows ensure accuracy at every step.",
      accent: "bg-emerald-100 text-emerald-700",
      Icon: ShieldCheck,
    },
    {
      title: "Improve Revenue",
      description: "Faster billing and better collection performance.",
      accent: "bg-violet-100 text-violet-700",
      Icon: BarChart3,
    },
    {
      title: "Better Experience",
      description: "Smoother journeys for patients and staff alike.",
      accent: "bg-amber-100 text-amber-800",
      Icon: Smile,
    },
  ];

  return (
    <section id="how-it-works" className="section-viewport scroll-mt-24 py-12 md:py-20 px-4 md:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={Route} tone="blue" />
            How It Works
          </span>
          <h2 className="mt-4 text-section font-bold text-secondary">
            A Patient&apos;s Journey,
            <br />
            <span className="bg-gradient-to-r from-accent to-blue-500 bg-clip-text text-transparent">
              Simplified.
            </span>
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-muted">
            See how Gestalt HIS streamlines every step of patient care, from
            appointment to follow-up.
          </p>
        </div>

        <motion.div
          variants={ctaFade}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="p-4 md:p-6"
        >
        <div className="hidden lg:grid grid-cols-5 gap-3 mb-4">
          {journeySteps.map((step, index) => (
            <motion.div
              key={step.number}
              className="relative flex items-center justify-center"
              variants={stepRevealVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.8 }}
              custom={index}
            >
              <span
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${step.accent}`}
              >
                {step.number}
              </span>
              {index < journeySteps.length - 1 && (
                <motion.span
                  className="absolute left-[58%] top-1/2 h-px w-[88%] origin-left -translate-y-1/2 border-t border-dashed border-slate-300"
                  variants={connectorRevealVariants}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.8 }}
                  custom={index}
                />
              )}
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {journeySteps.map((step, index) => {
            const StepIcon = step.Icon;
            return (
              <motion.article
                variants={stepRevealVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                key={step.number}
                custom={index}
                className={`relative flex gap-3 p-4 transition hover:-translate-y-1 xl:flex-col xl:gap-0 ${
                  index < journeySteps.length - 1
                    ? "border-b border-slate-200 pb-6 md:pb-4 xl:border-b-0 xl:pb-4 xl:after:absolute xl:after:-right-1.5 xl:after:top-4 xl:after:h-[calc(100%-2rem)] xl:after:w-px xl:after:bg-slate-200 xl:after:content-['']"
                    : ""
                }`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${step.accent}`}
                >
                  <StepIcon className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className={`text-base font-semibold ${step.iconAccent}`}>
                    {step.title}
                  </h4>
                  <p className="mt-1.5 text-sm text-muted leading-6 xl:mt-2">
                    {step.description}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
        </motion.div>

        <div className="relative isolate mt-7 overflow-hidden rounded-2xl bg-neutral-900 px-6 py-8 text-white shadow-xl md:px-10 md:py-10">
          <div
            className="pointer-events-none absolute inset-0 -z-20 bg-cover bg-center opacity-[0.22]"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/software-cta-bg.png)`,
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-neutral-950/95 via-neutral-900/90 to-neutral-950/88"
            aria-hidden
          />

          <motion.div
            variants={ctaStagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_2fr] lg:items-center lg:gap-8"
          >
            <motion.div
              variants={ctaFade}
              className="flex flex-row items-start gap-3 lg:flex-col lg:gap-0"
            >
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-accent-light text-white">
                <Smile className="h-7 w-7" strokeWidth={1.75} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 text-white lg:flex-none">
                <h3 className="font-display text-xl font-bold text-white md:text-2xl">
                  Better care. Smarter operations.
                </h3>
                <p className="mt-2 font-display text-xl font-bold text-primary md:text-2xl [text-shadow:0_1px_2px_rgba(10,10,10,0.45)]">
                  Happier patients.
                </p>
              </div>
            </motion.div>
            <motion.div
              variants={ctaFade}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  variants={ctaFade}
                  key={benefit.title}
                  className={[
                    "flex gap-3 p-4 sm:px-5 lg:flex-col lg:gap-0 lg:px-6",
                    index > 0 && "border-white/15 max-lg:border-t",
                    index % 2 === 1 && "sm:max-lg:border-l sm:max-lg:border-t-0",
                    index >= 2 && "sm:max-lg:border-t",
                    index > 0 && "lg:border-l lg:border-t-0",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${benefit.accent}`}
                  >
                    <benefit.Icon className="size-5" strokeWidth={1.9} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-base font-semibold text-white lg:mt-2">
                      {benefit.title}
                    </h4>
                    <p className="mt-1 text-sm leading-6 text-neutral-200 lg:mt-1">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
